import React, { useState, useEffect } from 'react'
import { 
  FileText, Copy, Edit3, CheckCircle, RefreshCw, 
  Send, Download, AlertTriangle
} from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

export default function DeclarationAssureurBlock({ sinistreId, onBrouillonGenere }) {
  const [loading, setLoading] = useState(false)
  const [brouillon, setBrouillon] = useState(null)
  const [error, setError] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [editedContent, setEditedContent] = useState('')

  useEffect(() => {
    fetchExistingBrouillon()
  }, [sinistreId])

  async function fetchExistingBrouillon() {
    try {
      const res = await fetch(`${API_URL}/api/ia/analyses/${sinistreId}`)
      const data = await res.json()
      
      if (data.analyses?.length > 0) {
        const brouillonAnalyse = data.analyses.find(a => a.type_analyse === 'brouillon_declaration')
        if (brouillonAnalyse?.brouillon_declaration) {
          setBrouillon(brouillonAnalyse.brouillon_declaration)
          setEditedContent(brouillonAnalyse.brouillon_declaration.corps_lettre || '')
        }
      }
    } catch (err) {
      console.error('Error fetching brouillon:', err)
    }
  }

  async function genererBrouillon() {
    setLoading(true)
    setError(null)
    
    try {
      const res = await fetch(`${API_URL}/api/ia/brouillon-declaration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sinistre_id: sinistreId })
      })
      
      const data = await res.json()
      
      if (data.erreur) {
        setError(data.erreur)
        toast.error('Erreur lors de la génération')
      } else {
        setBrouillon(data)
        setEditedContent(data.corps_lettre || '')
        toast.success('Brouillon généré')
        onBrouillonGenere?.()
      }
    } catch (err) {
      setError('Erreur de connexion au service IA')
      toast.error('Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text)
    toast.success('Copié dans le presse-papier')
  }

  function formatFullLetter() {
    if (!brouillon) return ''
    
    return `
${brouillon.objet}

${new Date().toLocaleDateString('fr-FR')}

Madame, Monsieur,

${editMode ? editedContent : brouillon.corps_lettre}

Pièces jointes:
${(brouillon.pieces_jointes || []).map(p => `- ${p}`).join('\n')}

${brouillon.formule_finale || 'Veuillez agréer, Madame, Monsieur, l\'expression de nos salutations distinguées.'}
    `.trim()
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="declaration-assureur-block">
      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <FileText size={16} className="text-blue-600" />
          Déclaration Assureur
        </h2>
        <button
          onClick={genererBrouillon}
          disabled={loading}
          data-testid="generer-brouillon-btn"
          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
        >
          {loading ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <RefreshCw size={12} />
          )}
          {brouillon ? 'Regénérer' : 'Générer'}
        </button>
      </div>

      <div className="p-5">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {!brouillon && !loading && !error && (
          <div className="text-center py-6">
            <Send size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">Aucun brouillon disponible</p>
            <p className="text-xs text-slate-400 mt-1">Cliquez sur "Générer" pour créer un brouillon de déclaration</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-6">
            <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Génération en cours...</p>
            <p className="text-xs text-slate-400 mt-1">L'IA rédige le brouillon de déclaration</p>
          </div>
        )}

        {brouillon && !loading && (
          <div className="space-y-4">
            {/* Objet */}
            <div>
              <p className="text-xs text-slate-400 mb-1">Objet</p>
              <p className="text-sm font-medium text-slate-800 bg-slate-50 px-3 py-2 rounded-lg">
                {brouillon.objet}
              </p>
            </div>

            {/* Résumé */}
            <div>
              <p className="text-xs text-slate-400 mb-1">Résumé du sinistre</p>
              <p className="text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg">
                {brouillon.resume_sinistre}
              </p>
            </div>

            {/* Corps de lettre */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-slate-400">Corps de la lettre</p>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Edit3 size={12} />
                  {editMode ? 'Annuler' : 'Modifier'}
                </button>
              </div>
              
              {editMode ? (
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              ) : (
                <div className="text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg whitespace-pre-wrap max-h-60 overflow-y-auto">
                  {brouillon.corps_lettre}
                </div>
              )}
            </div>

            {/* Pièces jointes */}
            {brouillon.pieces_jointes?.length > 0 && (
              <div>
                <p className="text-xs text-slate-400 mb-1">Pièces à joindre</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  {brouillon.pieces_jointes.map((piece, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle size={12} className="text-green-500" />
                      {piece}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
              <button
                onClick={() => copyToClipboard(formatFullLetter())}
                className="flex-1 px-3 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 flex items-center justify-center gap-2 transition-colors"
              >
                <Copy size={14} />
                Copier le courrier
              </button>
            </div>

            {/* Disclaimer */}
            <div className="text-xs text-slate-400 italic pt-2">
              {brouillon.disclaimer || 'Ce brouillon est généré automatiquement. Veuillez le relire et le valider avant envoi.'}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
